import { useMemo } from 'react';
import type {
	Command,
	CommandInternal,
	CommandSections,
} from '../../../../common/types/commands/index.tsx';
import { getFlattenedResults } from '../../../../common/utils/get-flattened-results/index.tsx';
import { getGroupedCommands } from '../../../../common/utils/get-grouped-commands/index.tsx';
import { useCommandPalette } from '../../../command-palette/index.tsx';
import { filterCommandsFromSearch } from '../filter-commands-from-search/index.tsx';
import { createFuseFromCommands, searchFuseIndex } from '../search-commands/main.tsx';
import { useSearchableCommands } from '../search-commands/utils/use-searchable-commands/index.tsx';
import { sortCommands } from '../sort-commands/index.tsx';

export const useSortAndFilter = (
	search: string,
	selectedCommands: Command[],
	selectedSections?: CommandSections,
	filterCommandsOverride?: (
		search: string,
		selectedCommands: Command[],
		selectedSections?: CommandSections,
	) => Command[],
) => {
	const queryKeywordCount = useMemo(
		() => search.trim().split(' ').filter(Boolean).length,
		[search],
	);
	const searchableCommands = useSearchableCommands(selectedCommands);
	const [{ activeIndex }] = useCommandPalette();
	const fuse = useMemo(
		() => createFuseFromCommands(searchableCommands, queryKeywordCount),
		[searchableCommands, queryKeywordCount],
	);
	const sortedAndFilteredCommands = useMemo((): CommandInternal[] => {
		if (filterCommandsOverride) {
			return filterCommandsOverride(search, selectedCommands, selectedSections);
		}
		let filteredCommands;
		if (!search.trim().toLowerCase().length) {
			filteredCommands = selectedCommands.map((command) => ({
				...command,
				searchRelevancyScore: 1,
			}));
		} else {
			const fuseResults = searchFuseIndex(fuse, search);
			filteredCommands = filterCommandsFromSearch(search, fuseResults, searchableCommands);
		}
		return sortCommands(filteredCommands, selectedSections);
	}, [
		filterCommandsOverride,
		fuse,
		search,
		searchableCommands,
		selectedCommands,
		selectedSections,
	]);
	const groupedCommands = useMemo(
		() => getGroupedCommands(sortedAndFilteredCommands, selectedSections),
		[selectedSections, sortedAndFilteredCommands],
	);

	const commandMatches = useMemo(
		() => getFlattenedResults(groupedCommands, activeIndex),
		[groupedCommands, activeIndex],
	);

	return commandMatches;
};
